import { Box, Typography, Avatar, Button } from '@mui/material'
import { AutoAwesome, Download, OpenInNew } from '@mui/icons-material'
import Markdown from 'react-markdown'

const isDownloadLink = (url) => {
  if (!url) return false
  const lower = url.toLowerCase()
  return ['.csv', '.pdf', '.xlsx', '.xls', '.zip', '.doc', '.docx'].some(ext => lower.includes(ext))
}

const getDownloadLabel = (url, linkText) => {
  const lower = (url || '').toLowerCase()
  if (lower.includes('.csv')) return 'Baixar CSV'
  if (lower.includes('.pdf')) return 'Baixar PDF'
  if (lower.includes('.xlsx') || lower.includes('.xls')) return 'Baixar Excel'
  if (lower.includes('.zip')) return 'Baixar ZIP'
  if (lower.includes('.doc') || lower.includes('.docx')) return 'Baixar Documento'
  if (linkText && linkText.trim()) return linkText.trim()
  return 'Abrir link'
}

// Componentes customizados para o react-markdown
const markdownComponents = {
  // Links renderizados como botões de download
  a: ({ href, children }) => {
    const linkText = typeof children === 'string' ? children : ''
    const label = getDownloadLabel(href, linkText)
    const isDownload = isDownloadLink(href)

    return (
      <Button
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="outlined"
        size="small"
        startIcon={isDownload ? <Download /> : <OpenInNew />}
        sx={{ my: 0.5, mx: 0.5, display: 'inline-flex', textTransform: 'none' }}
      >
        {label}
      </Button>
    )
  },
  // Parágrafos sem margem excessiva
  p: ({ children }) => (
    <Typography variant="body2" component="p" sx={{ my: 0.5, '&:first-of-type': { mt: 0 }, '&:last-of-type': { mb: 0 } }}>
      {children}
    </Typography>
  ),
  // Listas estilizadas
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 2.5, my: 0.5 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ pl: 2.5, my: 0.5 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Typography variant="body2" component="li" sx={{ mb: 0.25 }}>
      {children}
    </Typography>
  ),
  // Negrito e itálico
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600 }}>{children}</Box>
  ),
  // Code inline
  code: ({ children, className }) => {
    const isBlock = className?.includes('language-')
    if (isBlock) {
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: 'grey.100',
            borderRadius: 1,
            p: 1.5,
            my: 1,
            overflow: 'auto',
            fontSize: '0.8rem',
            fontFamily: 'monospace',
          }}
        >
          <code>{children}</code>
        </Box>
      )
    }
    return (
      <Box
        component="code"
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 0.5,
          px: 0.5,
          py: 0.25,
          fontSize: '0.85em',
          fontFamily: 'monospace',
        }}
      >
        {children}
      </Box>
    )
  },
  // Tabelas
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 1 }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.85rem',
          '& th, & td': {
            border: '1px solid',
            borderColor: 'grey.300',
            px: 1,
            py: 0.5,
            textAlign: 'left',
          },
          '& th': {
            bgcolor: 'grey.100',
            fontWeight: 600,
          },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
  // Blockquote
  blockquote: ({ children }) => (
    <Box
      sx={{
        borderLeft: 3,
        borderColor: 'primary.main',
        pl: 1.5,
        my: 1,
        color: 'text.secondary',
      }}
    >
      {children}
    </Box>
  ),
  // Headings menores para caber no chat
  h1: ({ children }) => (
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>{children}</Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>{children}</Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="body2" fontWeight="bold" sx={{ mt: 0.5, mb: 0.25 }}>{children}</Typography>
  ),
  // Horizontal rule
  hr: () => (
    <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'grey.300', my: 1 }} />
  ),
}

const ChatMessage = ({ message, user }) => {
  const isUser = message.role === 'user'

  const userName = user?.first_name || ''
  const userInitial = userName ? userName.charAt(0).toUpperCase() : '?'

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
        gap: 1,
      }}
    >
      {!isUser && (
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', flexShrink: 0 }}>
          <AutoAwesome sx={{ fontSize: 18 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: '80%' }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', mb: 0.5, display: 'block', textAlign: isUser ? 'right' : 'left' }}
        >
          {isUser ? userName : 'Assistente BlocoB3'}
        </Typography>
        <Box
          sx={{
            p: 1.5,
            borderRadius: isUser ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
            bgcolor: isUser ? 'grey.200' : 'grey.50',
            color: 'text.primary',
          }}
        >
          {isUser ? (
            <Typography
              variant="body2"
              component="div"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {message.content}
            </Typography>
          ) : (
            <Box sx={{ wordBreak: 'break-word', '& > *:first-of-type': { mt: 0 }, '& > *:last-child': { mb: 0 } }}>
              <Markdown components={markdownComponents}>
                {message.content}
              </Markdown>
            </Box>
          )}
          <Typography
            variant="caption"
            sx={{ opacity: 0.7, display: 'block', mt: 0.5, textAlign: 'right' }}
          >
            {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        </Box>
      </Box>
      {isUser && (
        <Avatar
          src={user?.photo || undefined}
          sx={{ width: 32, height: 32, bgcolor: 'grey.500', flexShrink: 0, fontSize: 14 }}
        >
          {!user?.photo && userInitial}
        </Avatar>
      )}
    </Box>
  )
}

export default ChatMessage
